import { type AnalysisScope } from "@/shared/domain/agentThread";

/**
 * How the launch page's analysis-window inputs are interpreted:
 * `datetime` reads them as `datetime-local` values in the user's local
 * timezone; `iso` as ISO 8601 timestamps (UTC with `Z`, explicit offset,
 * or local when offset-less; fraction up to ns precision); `ns` as
 * integer nanoseconds since the Unix epoch (for pasting exact log
 * timestamps).
 */
export type WindowInputMode = "datetime" | "iso" | "ns";

/**
 * Outcome of interpreting the launch form's optional analysis-window
 * inputs: either a wire-ready scope (`null` when both inputs are empty,
 * meaning "omit the field"), or a user-facing validation error.
 */
export type AnalysisScopeResult =
  { scope: AnalysisScope | null; error: null } | { scope: null; error: string };

const NS_PER_MS = 1_000_000n;
const NS_PER_SECOND = 1_000_000_000n;

/** Matches an unsigned integer — the only shape accepted in ns mode.
 *  Anchored so floats, signs, exponents, and embedded whitespace all fail
 *  loudly instead of being partially parsed. */
const NS_VALUE_RE = /^\d+$/;

/** Matches the ISO 8601 shapes the iso mode accepts: a date, optionally
 *  followed by a time (seconds optional), a fraction of up to 9 digits
 *  (ns precision — a longer fraction is rejected rather than truncated,
 *  since sub-ns digits are almost certainly a mangled paste), and a
 *  timezone (`Z` or `±HH:MM`, only meaningful after a time). Capture
 *  groups: 1 = date+time without fraction/zone, 2 = fraction digits,
 *  3 = timezone. Anchored so `Date.parse`'s looser grammar never sees
 *  anything this regex didn't vet. */
const ISO_VALUE_RE =
  /^(\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2})?)?)(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})?$/;

/** Validates structure the regex alone can't: a fraction requires seconds
 *  (`T03:04.5` is not ISO 8601) and a timezone requires a time
 *  (`2026-01-02Z` is not a valid instant). */
function isWellFormedIso(
  dateTime: string,
  fraction: string | undefined,
  zone: string | undefined,
): boolean {
  const hasTime = dateTime.includes("T");
  const hasSeconds = /T\d{2}:\d{2}:\d{2}$/.test(dateTime);
  if (fraction !== undefined && !hasSeconds) {
    return false;
  }
  if (zone !== undefined && !hasTime) {
    return false;
  }
  return true;
}

/**
 * Converts one window-bound input to nanoseconds since the Unix epoch.
 * Returns `null` for an empty input (that bound is unconstrained) and
 * `"invalid"` for a value the given mode cannot parse. `bigint` is used
 * throughout because ns-epoch values exceed `Number.MAX_SAFE_INTEGER` —
 * a pasted log timestamp must survive to the wire digit-for-digit.
 */
function toEpochNs(
  input: string,
  mode: WindowInputMode,
): bigint | null | "invalid" {
  const trimmed = input.trim();
  if (trimmed === "") {
    return null;
  }
  if (mode === "ns") {
    return NS_VALUE_RE.test(trimmed) ? BigInt(trimmed) : "invalid";
  }
  if (mode === "iso") {
    const match = ISO_VALUE_RE.exec(trimmed);
    if (match === null) {
      return "invalid";
    }
    const [, dateTime, fraction, zone] = match;
    if (!isWellFormedIso(dateTime, fraction, zone)) {
      return "invalid";
    }
    // Parse the whole-second instant with the platform (which applies
    // the ISO timezone rules: `Z`/offset as given, offset-less date-time
    // as local, date-only as UTC), then add the fraction with string
    // arithmetic — `Date` only carries ms, so feeding it the fraction
    // would round the last six digits away.
    const ms = new Date(`${dateTime}${zone ?? ""}`).getTime();
    if (Number.isNaN(ms)) {
      return "invalid";
    }
    return (
      BigInt(ms) * NS_PER_MS + BigInt((fraction ?? "").padEnd(9, "0") || "0")
    );
  }
  const ms = new Date(trimmed).getTime();
  if (Number.isNaN(ms)) {
    return "invalid";
  }
  return BigInt(ms) * NS_PER_MS;
}

const INVALID_VALUE_MESSAGE: Record<WindowInputMode, string> = {
  datetime: "Enter a valid date and time.",
  iso: "Enter an ISO 8601 timestamp, e.g. 2026-06-11T12:45:30Z.",
  ns: "Enter a whole number of nanoseconds since the Unix epoch.",
};

/**
 * Builds the launch request's `analysis_scope` from the two optional
 * window-bound inputs on the launch page. Lives outside the component
 * (mirroring `seedLaunchValues`) so the empty/one-sided/inverted-window
 * rules are unit-testable without mounting the page.
 */
export function buildAnalysisScope(
  startInput: string,
  endInput: string,
  mode: WindowInputMode,
): AnalysisScopeResult {
  const start = toEpochNs(startInput, mode);
  const end = toEpochNs(endInput, mode);
  if (start === "invalid" || end === "invalid") {
    return { scope: null, error: INVALID_VALUE_MESSAGE[mode] };
  }
  if (start !== null && end !== null && start > end) {
    return {
      scope: null,
      error: "Analysis window start must be at or before its end.",
    };
  }
  if (start === null && end === null) {
    return { scope: null, error: null };
  }
  return {
    scope: {
      ...(start !== null ? { start_time: start } : {}),
      ...(end !== null ? { end_time: end } : {}),
    },
    error: null,
  };
}

/** Formats a date as a `datetime-local` value with seconds precision,
 *  in the user's local timezone (the reading the control displays). */
function toLocalDateTimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return `${datePart}T${timePart}`;
}

/**
 * Carries one window-bound input across a mode switch so the user's
 * entered bound survives toggling the format. A value the source mode
 * cannot parse (or that doesn't fit a Date, e.g. an absurdly large ns
 * count) converts to `""` — the user starts that bound fresh rather
 * than inheriting garbage in the new format. ns → datetime truncates
 * to whole seconds, the finest granularity the datetime control shows.
 */
export function convertWindowValue(
  value: string,
  from: WindowInputMode,
  to: WindowInputMode,
): string {
  if (from === to) {
    return value;
  }
  const ns = toEpochNs(value, from);
  if (ns === null || ns === "invalid") {
    return "";
  }
  if (to === "ns") {
    return ns.toString();
  }
  if (to === "iso") {
    // Floor-divide so a (theoretical) pre-epoch instant still splits into
    // a whole second plus a non-negative fraction.
    let seconds = ns / NS_PER_SECOND;
    let remainder = ns % NS_PER_SECOND;
    if (remainder < 0n) {
      seconds -= 1n;
      remainder += NS_PER_SECOND;
    }
    const date = new Date(Number(seconds) * 1000);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    const base = date.toISOString().replace(".000Z", "");
    const fraction = remainder.toString().padStart(9, "0").replace(/0+$/, "");
    return fraction === "" ? `${base}Z` : `${base}.${fraction}Z`;
  }
  const wholeSecondMs = Number((ns / NS_PER_MS / 1000n) * 1000n);
  const date = new Date(wholeSecondMs);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return toLocalDateTimeString(date);
}
